import { EditableContent } from '../../../edit/EditableContent';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName } from '../MenuItemTags';
import { isMenuItemActive } from '../menuEditHelpers';
import { readHighlightMenuSection } from './highlightMenuItems';

/** Numbered chef’s picks — calm typography-led fine dining stories. */
export function MenuHighlightChefsCut({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const { sectionTitle, sectionSubtitle, dishes } = readHighlightMenuSection(
    block,
    locale,
  );
  const target = isActive ? activeEditTarget : null;
  const picks = dishes.slice(0, 5);

  return (
    <section
      className={['ws-hl-chefs', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-hl-chefs__inner">
        <header className="ws-hl-chefs__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-hl-chefs__title"
          >
            {sectionTitle}
          </EditableContent>
          {sectionSubtitle || editable ? (
            <EditableContent
              as="p"
              fieldKey="sectionSubtitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-hl-chefs__subtitle"
            >
              {sectionSubtitle}
            </EditableContent>
          ) : null}
        </header>
        <ol className="ws-hl-chefs__list">
          {picks.map(({ item, columnIndex, itemIndex }, index) => (
            <li
              key={`${columnIndex}-${itemIndex}`}
              className={[
                'ws-hl-chefs__item',
                isMenuItemActive(
                  target?.kind === 'menu-item' ? target : null,
                  columnIndex,
                  itemIndex,
                )
                  ? 'ws-edit-child--active'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              {...(editable
                ? {
                    'data-edit-child': 'menu-item' as const,
                    'data-column-index': columnIndex,
                    'data-item-index': itemIndex,
                  }
                : {})}
            >
              <span className="ws-hl-chefs__num" aria-hidden>
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="ws-hl-chefs__body">
                <div className="ws-hl-chefs__head">
                  <MenuItemName
                    item={item}
                    locale={locale}
                    className="ws-hl-chefs__name"
                    showLegalMarks={false}
                    showOrderCode={false}
                  />
                  <MenuItemDietaryTags
                    item={item}
                    locale={locale}
                    iconsOnly
                    placement="inline"
                  />
                </div>
                {item.description ? (
                  <p className="ws-hl-chefs__desc">{item.description}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
